import React, { useEffect, useState } from "react"
import { MDBDataTable } from "mdbreact"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
} from "reactstrap"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { clientsAPI, usersAPI } from "../../services/api"
import AlertModal from "../../components/Common/AlertModal"

const ClientList = (props) => {
    document.title = "Liste des Clients | Ticket Management"

    const [clients, setClients] = useState([])
    const [users, setUsers] = useState([])
    const [modal, setModal] = useState(false)
    const [editingClient, setEditingClient] = useState(null)
    const [createUserAccount, setCreateUserAccount] = useState(false)

    const [formData, setFormData] = useState({
        nom: "",
        ice: "",
        identifiant_fiscal: "",
        telephone: "",
        email: "",
        adresse: "",
        user_id: "",
        // Champs pour création de compte
        user_name: "",
        user_email: "",
        user_password: "",
    })

    const [alertConfig, setAlertConfig] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        title: ''
    })

    const toggleAlert = () => setAlertConfig({ ...alertConfig, isOpen: !alertConfig.isOpen })

    const breadcrumbItems = [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Clients", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Liste des Clients", breadcrumbItems)
        fetchData()
        fetchUsers()
    }, [])

    const fetchData = async () => {
        try {
            const res = await clientsAPI.getAll()
            setClients(res.data)
        } catch (error) {
            console.error("Erreur lors du chargement des clients", error)
        }
    }

    const fetchUsers = async () => {
        try {
            const res = await usersAPI.getAll()
            // Filtrer uniquement les utilisateurs avec le rôle 'client' qui ne sont pas encore liés à un client
            const clientUsers = res.data.filter(u => u.role === 'client')
            setUsers(clientUsers)
        } catch (error) {
            console.error("Erreur lors du chargement des utilisateurs", error)
        }
    }

    const toggleModal = () => {
        setModal(!modal)
        if (!modal) {
            setEditingClient(null)
            setCreateUserAccount(false)
            setFormData({
                nom: "",
                ice: "",
                identifiant_fiscal: "",
                telephone: "",
                email: "",
                adresse: "",
                user_id: "",
                user_name: "",
                user_email: "",
                user_password: "",
            })
        }
    }

    const handleEdit = (client) => {
        setEditingClient(client)
        setFormData({
            nom: client.nom,
            ice: client.ice || "",
            identifiant_fiscal: client.identifiant_fiscal || "",
            telephone: client.telephone || "",
            email: client.email || "",
            adresse: client.adresse || "",
            user_id: client.user_id || "",
            user_name: "",
            user_email: "",
            user_password: "",
        })
        setModal(true)
    }

    const handleDelete = async (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) {
            try {
                await clientsAPI.delete(id)
                fetchData()
            } catch (error) {
                console.error("Erreur lors de la suppression", error)
            }
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            let userId = formData.user_id

            // Si on crée un nouveau compte utilisateur
            if (createUserAccount && !editingClient) {
                const newUser = await usersAPI.create({
                    name: formData.user_name,
                    email: formData.user_email,
                    password: formData.user_password,
                    role: 'client',
                    telephone: formData.telephone,
                })
                userId = newUser.data.id
            }

            const clientData = {
                nom: formData.nom,
                ice: formData.ice,
                identifiant_fiscal: formData.identifiant_fiscal,
                telephone: formData.telephone,
                email: formData.email,
                adresse: formData.adresse,
                user_id: userId || null,
            }

            if (editingClient) {
                await clientsAPI.update(editingClient.id, clientData)
            } else {
                await clientsAPI.create(clientData)
            }
            toggleModal()
            setAlertConfig({
                isOpen: true,
                type: 'success',
                title: editingClient ? 'Client modifié' : 'Client créé',
                message: editingClient ? "Les informations du client ont été mises à jour avec succès." : "Le client a été créé avec succès."
            })
            fetchData()
            fetchUsers()
        } catch (error) {
            console.error("Erreur lors de l'enregistrement", error)
            setAlertConfig({
                isOpen: true,
                type: 'error',
                title: 'Erreur',
                message: "Une erreur est survenue lors de l'enregistrement du client."
            })
        }
    }

    const data = {
        columns: [
            { label: "Nom", field: "nom", sort: "asc" },
            { label: "ICE", field: "ice", sort: "asc" },
            { label: "IF", field: "identifiant_fiscal", sort: "asc" },
            { label: "Téléphone", field: "telephone", sort: "asc" },
            { label: "Email", field: "email", sort: "asc" },
            { label: "Compte", field: "compte", sort: "disabled" },
            { label: "Actions", field: "actions", sort: "disabled" },
        ],
        rows: clients.map((c) => ({
            nom: c.nom,
            ice: c.ice || "-",
            identifiant_fiscal: c.identifiant_fiscal || "-",
            telephone: c.telephone || "-",
            email: c.email || "-",
            compte: c.user ? (
                <span className="badge bg-success">
                    <i className="mdi mdi-account-check me-1"></i>
                    {c.user.name}
                </span>
            ) : (
                <span className="badge bg-secondary">
                    <i className="mdi mdi-account-off me-1"></i>
                    Non lié
                </span>
            ),
            actions: (
                <div className="d-flex gap-2">
                    <Button color="primary" size="sm" onClick={() => handleEdit(c)}>
                        <i className="mdi mdi-pencil"></i>
                    </Button>
                    <Button color="danger" size="sm" onClick={() => handleDelete(c.id)}>
                        <i className="mdi mdi-trash-can"></i>
                    </Button>
                </div>
            ),
        })),
    }

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <CardTitle className="h4">Liste des Clients</CardTitle>
                                <Button color="success" onClick={toggleModal}>
                                    <i className="mdi mdi-plus me-1"></i> Nouveau Client
                                </Button>
                            </div>

                            <MDBDataTable
                                noBottomColumns
                                responsive
                                striped
                                bordered
                                data={data}
                                entriesLabel="Afficher"
                                searchLabel="Rechercher"
                                infoLabel={["Affichage de", "à", "sur", "entrées"]}
                                paginationLabel={["Précédent", "Suivant"]}
                                noRecordsFoundLabel="Aucun enregistrement trouvé"
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={modal} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>
                    {editingClient ? "Modifier le Client" : "Nouveau Client"}
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label for="nom">Nom du Client / Entreprise</Label>
                                    <Input
                                        type="text"
                                        id="nom"
                                        value={formData.nom}
                                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="ice">ICE</Label>
                                    <Input
                                        type="text"
                                        id="ice"
                                        value={formData.ice}
                                        onChange={(e) => setFormData({ ...formData, ice: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="identifiant_fiscal">Identifiant Fiscal (IF)</Label>
                                    <Input
                                        type="text"
                                        id="identifiant_fiscal"
                                        value={formData.identifiant_fiscal}
                                        onChange={(e) => setFormData({ ...formData, identifiant_fiscal: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="telephone">Téléphone</Label>
                                    <Input
                                        type="text"
                                        id="telephone"
                                        value={formData.telephone}
                                        onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="email">Email</Label>
                                    <Input
                                        type="email"
                                        id="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label for="adresse">Adresse</Label>
                            <Input
                                type="textarea"
                                id="adresse"
                                rows="2"
                                value={formData.adresse}
                                onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                            />
                        </FormGroup>

                        <hr className="my-4" />

                        <h5 className="mb-3">Compte Utilisateur</h5>

                        {!editingClient && (
                            <FormGroup check className="mb-3">
                                <Input
                                    type="checkbox"
                                    id="createUserAccount"
                                    checked={createUserAccount}
                                    onChange={(e) => {
                                        setCreateUserAccount(e.target.checked)
                                        if (!e.target.checked) {
                                            setFormData({
                                                ...formData,
                                                user_name: "",
                                                user_email: "",
                                                user_password: "",
                                            })
                                        }
                                    }}
                                />
                                <Label check for="createUserAccount">
                                    Créer un nouveau compte utilisateur pour ce client
                                </Label>
                            </FormGroup>
                        )}

                        {!createUserAccount ? (
                            <FormGroup>
                                <Label for="user_id">Utilisateur existant (optionnel)</Label>
                                <Input
                                    type="select"
                                    id="user_id"
                                    value={formData.user_id}
                                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                                >
                                    <option value="">-- Aucun utilisateur lié --</option>
                                    {users.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.name} ({user.email})
                                        </option>
                                    ))}
                                </Input>
                                <small className="text-muted">
                                    Sélectionnez un utilisateur existant avec le rôle "client"
                                </small>
                            </FormGroup>
                        ) : (
                            <>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="user_name">Nom complet de l'utilisateur *</Label>
                                            <Input
                                                type="text"
                                                id="user_name"
                                                value={formData.user_name}
                                                onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                                                required={createUserAccount}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="user_email">Email de connexion *</Label>
                                            <Input
                                                type="email"
                                                id="user_email"
                                                value={formData.user_email}
                                                onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                                                required={createUserAccount}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="user_password">Mot de passe *</Label>
                                            <Input
                                                type="password"
                                                id="user_password"
                                                value={formData.user_password}
                                                onChange={(e) => setFormData({ ...formData, user_password: e.target.value })}
                                                required={createUserAccount}
                                                minLength={8}
                                            />
                                            <small className="text-muted">Minimum 8 caractères</small>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>Annuler</Button>
                        <Button color="primary" type="submit">Enregistrer</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <AlertModal
                isOpen={alertConfig.isOpen}
                toggle={toggleAlert}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
            />
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(ClientList)
